import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useArtistAuth } from '../../hooks/useArtistAuth';
import { supabase } from '../../utils/supabaseClient';
import { 
  Search, 
  Filter, 
  Calendar, 
  DollarSign, 
  Clock, 
  Eye, 
  Plus,
  CheckCircle,
  AlertCircle,
  FileText,
  TrendingUp,
  Palette
} from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';
import '../../styles/SubmissionSystem.css';

const ArtistSubmissionDashboard = () => {
  const { user, isArtist, loading: authLoading } = useArtistAuth();
  const [submissions, setSubmissions] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [showAccessRequired, setShowAccessRequired] = useState(false);
  const [stats, setStats] = useState({
    totalSubmissions: 0,
    activeApplications: 0,
    completedSubmissions: 0,
    upcomingDeadlines: 0
  });

  const PAGE_SIZE = 12;

  // Add a small delay to prevent rapid flashing between auth states
  useEffect(() => {
    console.log('ArtistSubmissionDashboard: Auth state changed:', { 
      user: !!user, 
      userId: user?.id, 
      isArtist, 
      authLoading,
      showAccessRequired 
    });
    
    if (!user || !isArtist) {
      const timer = setTimeout(() => {
        console.log('ArtistSubmissionDashboard: Setting showAccessRequired to true');
        setShowAccessRequired(true);
      }, 200); // Reduced from 500ms to 200ms for better responsiveness
      
      return () => clearTimeout(timer);
    } else {
      console.log('ArtistSubmissionDashboard: Setting showAccessRequired to false');
      setShowAccessRequired(false);
    }
  }, [user, isArtist, authLoading]);

  useEffect(() => {
    if (user && isArtist) {
      fetchData();
    }
  }, [user, isArtist, currentPage, searchQuery, selectedFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch available submissions using simple queries
      let submissionsData = null;
      let applicationsData = null;
      
      try {
        // Simple query to get all submissions first with timeout
        const submissionsPromise = supabase
          .from('submissions_details')
          .select(`
            submission_id,
            title,
            theme,
            submission_deadline,
            submission_price,
            cover_image,
            collection_id,
            gallery_id,
            created_at
          `)
          .order('submission_deadline', { ascending: false, nullsLast: true })
          .order('created_at', { ascending: false });
        
        const submissionsTimeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Submissions query timeout')), 5000)
        );
        
        const { data: allSubmissions, error: submissionsError } = await Promise.race([
          submissionsPromise,
          submissionsTimeout
        ]);
          
        if (submissionsError) throw submissionsError;
        
        // Get user's existing applications to filter out with timeout
        const applicationsPromise = supabase
          .from('artist_submission_mapping')
          .select('submission_id')
          .eq('user_id', user.id);
        
        const applicationsTimeout = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Applications query timeout')), 3000)
        );
        
        const { data: userApplications, error: appsError } = await Promise.race([
          applicationsPromise,
          applicationsTimeout
        ]);
          
        if (appsError) {
          console.warn('Could not fetch user applications:', appsError);
        }
        
        // Filter out submissions user has already applied to
        const appliedSubmissionIds = userApplications?.map(app => app.submission_id) || [];
        const availableSubmissions = allSubmissions?.filter(sub => 
          !appliedSubmissionIds.includes(sub.submission_id)
        ) || [];
        
        // Add collection names to submissions
        const submissionsWithCollections = await Promise.all(
          availableSubmissions.map(async (sub) => {
            let collectionName = 'N/A';
            if (sub.collection_id) {
              try {
                // Add timeout to collection query
                const collectionPromise = supabase
                  .from('collections')
                  .select('title')
                  .eq('collection_id', sub.collection_id)
                  .single();
                
                const collectionTimeout = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Collection query timeout')), 3000)
                );
                
                const { data: collectionData } = await Promise.race([collectionPromise, collectionTimeout]);
                collectionName = collectionData?.title || 'N/A';
              } catch (collectionError) {
                console.warn('Could not fetch collection details:', collectionError);
                collectionName = 'N/A';
              }
            }
            
            return {
              ...sub,
              collection_name: collectionName
            };
          })
        );

        // Sort submissions to prioritize future deadlines first
        const sortedSubmissions = submissionsWithCollections.sort((a, b) => {
          const now = new Date();
          const aDeadline = a.submission_deadline ? new Date(a.submission_deadline) : null;
          const bDeadline = b.submission_deadline ? new Date(b.submission_deadline) : null;
          
          // If both have deadlines
          if (aDeadline && bDeadline) {
            const aDaysUntil = Math.ceil((aDeadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            const bDaysUntil = Math.ceil((bDeadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            
            // If both are future deadlines, sort by closest deadline first
            if (aDaysUntil > 0 && bDaysUntil > 0) {
              return aDaysUntil - bDaysUntil;
            }
            
            // If one is future and one is past, prioritize future
            if (aDaysUntil > 0 && bDaysUntil <= 0) return -1;
            if (aDaysUntil <= 0 && bDaysUntil > 0) return 1;
            
            // If both are past deadlines, sort by most recent deadline first (closest to now)
            if (aDaysUntil <= 0 && bDaysUntil <= 0) {
              return Math.abs(aDaysUntil) - Math.abs(bDaysUntil);
            }
          }
          
          // If only one has a deadline, prioritize the one with deadline
          if (aDeadline && !bDeadline) return -1;
          if (!aDeadline && bDeadline) return 1;
          
          // If neither has deadline, sort by creation date (newest first)
          return new Date(b.created_at) - new Date(a.created_at);
        });
        
        // Apply pagination
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        const paginatedSubmissions = sortedSubmissions.slice(startIndex, endIndex);
        
        submissionsData = {
          submissions: paginatedSubmissions,
          total: availableSubmissions.length,
          has_more: endIndex < availableSubmissions.length
        };
        
      } catch (error) {
        console.error('Error fetching submissions:', error);
        submissionsData = {
          submissions: [],
          total: 0,
          has_more: false
        };
      }

      // Fetch user's applications using simple queries
      try {
        // Get user's applications
        const { data: applications, error: applicationsError } = await supabase
          .from('artist_submission_mapping')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(50);
          
        if (applicationsError) throw applicationsError;
        
        // Get submission details for each application
        const applicationsWithDetails = [];
        for (const app of applications || []) {
          try {
            const { data: submissionDetails, error: detailError } = await supabase
              .from('submissions_details')
              .select(`
                title, 
                theme, 
                submission_deadline, 
                submission_price, 
                cover_image, 
                collection_id
              `)
              .eq('submission_id', app.submission_id)
              .single();
              
            if (!detailError && submissionDetails) {
              // Get collection name if collection_id exists
              let collectionName = 'N/A';
              if (submissionDetails.collection_id) {
                try {
                  const { data: collectionData } = await supabase
                    .from('collections')
                    .select('title')
                    .eq('collection_id', submissionDetails.collection_id)
                    .single();
                  collectionName = collectionData?.title || 'N/A';
                } catch (collectionError) {
                  console.warn('Could not fetch collection details:', collectionError);
                }
              }
              
              applicationsWithDetails.push({
                id: app.id,
                submission_id: app.submission_id,
                status: app.status || 'submitted',
                submitted_at: app.created_at,
                created_at: app.created_at,
                updated_at: app.updated_at,
                title: submissionDetails.title,
                theme: submissionDetails.theme,
                submission_deadline: submissionDetails.submission_deadline,
                submission_price: submissionDetails.submission_price,
                cover_image: submissionDetails.cover_image,
                collection_name: collectionName
              });
            }
          } catch (detailError) {
            console.warn('Could not fetch details for submission:', app.submission_id, detailError);
          }
        }
        
        applicationsData = {
          applications: applicationsWithDetails,
          total: applicationsWithDetails.length,
          has_more: false
        };
        
      } catch (error) {
        console.error('Error fetching applications:', error);
        applicationsData = {
          applications: [],
          total: 0,
          has_more: false
        };
      }

      setSubmissions(submissionsData.submissions || []);
      setMyApplications(applicationsData.applications || []);
      setHasMore(submissionsData.has_more || false);

      // Calculate stats
      const activeApps = applicationsData.applications?.filter(app => 
        ['draft', 'submitted', 'under_review'].includes(app.status)
      ).length || 0;

      const completedApps = applicationsData.applications?.filter(app => 
        ['accepted', 'rejected'].includes(app.status)
      ).length || 0;

      const upcomingDeadlines = submissionsData.submissions?.filter(sub => 
        sub.days_until_deadline && sub.days_until_deadline <= 7
      ).length || 0;

      setStats({
        totalSubmissions: submissionsData.total || 0,
        activeApplications: activeApps,
        completedSubmissions: completedApps,
        upcomingDeadlines
      });

    } catch (error) {
      console.error('Error fetching data:', error);
      // Set empty data on error
      setSubmissions([]);
      setMyApplications([]);
      setHasMore(false);
      setStats({
        totalSubmissions: 0,
        activeApplications: 0,
        completedSubmissions: 0,
        upcomingDeadlines: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'under_review': return 'bg-purple-100 text-purple-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <FileText className="w-4 h-4" />;
      case 'submitted': return <Clock className="w-4 h-4" />;
      case 'under_review': return <Eye className="w-4 h-4" />;
      case 'accepted': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <AlertCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return 'No deadline';
    
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'Deadline passed';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    if (days <= 7) return `Due in ${days} days`;
    
    return new Date(deadline).toLocaleDateString();
  };

  const getDeadlineColor = (deadline) => {
    if (!deadline) return 'text-gray-500';
    
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'text-red-500';
    if (days <= 3) return 'text-red-600';
    if (days <= 7) return 'text-orange-600';
    if (days <= 14) return 'text-yellow-600';
    
    return 'text-green-600';
  };

  // Helper function to detect and render HTML content
  const renderHTMLContent = (content, className = 'html-content') => {
    if (!content) return null;
    
    // Check if the content contains HTML tags
    const htmlRegex = /<[^>]*>/;
    const isHTML = htmlRegex.test(content);
    
    if (isHTML) {
      return (
        <div 
          className={className} 
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    } else {
      return content;
    }
  };

  // Helper function to truncate description text with HTML support
  const truncateDescription = (text, maxLines) => {
    if (!text) return '';
    
    // Check if content contains HTML
    const htmlRegex = /<[^>]*>/;
    const isHTML = htmlRegex.test(text);
    
    if (isHTML) {
      // For HTML content, just return a truncated version without breaking tags
      // Remove HTML tags for length calculation
      const textOnly = text.replace(/<[^>]*>/g, '');
      const charsPerLine = 80;
      const maxChars = charsPerLine * maxLines;
      
      if (textOnly.length <= maxChars) {
        return text; // Return full HTML if short enough
      }
      
      // For longer HTML content, provide a truncated plain text version
      const truncated = textOnly.substring(0, maxChars);
      const lastSpace = truncated.lastIndexOf(' ');
      const finalText = lastSpace > maxChars * 0.8 ? 
        truncated.substring(0, lastSpace) : 
        truncated;
      
      return finalText + '...';
    } else {
      // Original logic for plain text
      const charsPerLine = 80;
      const maxChars = charsPerLine * maxLines;
      
      if (text.length <= maxChars) {
        return text;
      }
      
      // Find a good break point near the limit
      const truncated = text.substring(0, maxChars);
      const lastSpace = truncated.lastIndexOf(' ');
      const finalText = lastSpace > maxChars * 0.8 ? 
        truncated.substring(0, lastSpace) : 
        truncated;
      
      return finalText + '...';
    }
  };

  // Helper function to get deadline styling class
  const getDeadlineClass = (deadline) => {
    if (!deadline) return '';
    
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'urgent';
    if (days <= 3) return 'urgent';
    if (days <= 7) return 'warning';
    
    return '';
  };

  // Helper function to get days until deadline
  const getDaysUntilDeadline = (deadline) => {
    if (!deadline) return 0;
    
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return days < 0 ? 0 : days;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No deadline';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <div className="submission-loading-container">
        <LoadingSpinner />
      </div>
    );
  }

  // Show loading spinner while waiting for access required delay
  if (!user || !isArtist) {
    if (!showAccessRequired) {
      return (
        <div className="submission-loading-container">
          <LoadingSpinner />
        </div>
      );
    }
    
    return (
      <div className="submission-loading-container">
        <div className="submission-empty-state">
          <div className="submission-empty-icon">
            <Palette className="w-16 h-16" />
          </div>
          <h2 className="submission-empty-title">Artist Access Required</h2>
          <p className="submission-empty-description">
            This dashboard is only available to authenticated artists. Please sign in with your artist account.
          </p>
          <Link
            to="/login"
            className="submission-btn submission-btn-primary"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="submission-system-container">
      {/* Header */}
      <div className="submission-header">
        <div className="submission-header-content">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="submission-title">Artist Submissions</h1>
              <p className="submission-subtitle">Discover and apply to gallery submission opportunities</p>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="/artist/profile"
                className="submission-btn submission-btn-secondary"
              >
                <Eye className="w-4 h-4" />
                View Profile
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="submission-main-content">
        {/* Stats Section */}
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card available">
              <div className="stat-icon">
                <TrendingUp size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.totalSubmissions}</div>
                <div className="stat-label">Available Submissions</div>
              </div>
            </div>
            
            <div className="stat-card active">
              <div className="stat-icon">
                <FileText size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.activeApplications}</div>
                <div className="stat-label">Active Applications</div>
              </div>
            </div>
            
            <div className="stat-card completed">
              <div className="stat-icon">
                <CheckCircle size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.completedSubmissions}</div>
                <div className="stat-label">Completed</div>
              </div>
            </div>
            
            <div className="stat-card deadline">
              <div className="stat-icon">
                <Clock size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.upcomingDeadlines}</div>
                <div className="stat-label">Upcoming Deadlines</div>
              </div>
            </div>
          </div>
        </div>

        <div className="submission-grid submission-grid-2">
          <>
            {/* Main Content - Available Submissions */}
            <div className="submission-content-card">
              <div className="submission-content-card-header">
                <div className="flex items-center justify-between">
                  <h2 className="submission-content-card-title">Available Submissions</h2>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search submissions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="submission-form-input pl-10"
                      />
                    </div>
                    <select
                      value={selectedFilter}
                      onChange={(e) => setSelectedFilter(e.target.value)}
                      className="submission-form-input submission-form-select"
                    >
                      <option value="all">All Submissions</option>
                      <option value="urgent">Urgent (≤7 days)</option>
                      <option value="has_draft">Has Draft</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="submission-content-card-body">
                {loading ? (
                  <div className="submission-loading">
                    <div className="submission-spinner"></div>
                    Loading submissions...
                  </div>
                ) : submissions.length === 0 ? (
                  <div className="submission-empty-state">
                    <Palette className="submission-empty-icon" />
                    <h3 className="submission-empty-title">No submissions available</h3>
                    <p className="submission-empty-description">Check back later for new submission opportunities.</p>
                  </div>
                ) : (
                  <div className="submissions-grid">
                    {submissions.map((submission) => (
                      <div key={submission.submission_id} className="submission-card">
                        {/* Cover Image */}
                        {submission.cover_image && (
                          <div className="submission-image">
                            <img src={submission.cover_image} alt={submission.title} />
                          </div>
                        )}
                        
                        {/* Content */}
                        <div className="submission-content">
                          <div className="submission-header">
                            <h3 className="submission-title">{submission.title}</h3>
                            <div className="submission-price">${submission.submission_price}</div>
                          </div>
                          
                          {/* Truncated Description */}
                          <div className="submission-description">
                            <div className="description-text">
                              {(() => {
                                const content = submission.theme || submission.description || '';
                                const htmlRegex = /<[^>]*>/;
                                const isHTML = htmlRegex.test(content);
                                
                                if (isHTML) {
                                  return renderHTMLContent(content, 'submission-description-content');
                                } else {
                                  return truncateDescription(content, 5);
                                }
                              })()}
                            </div>
                          </div>
                          
                          {/* Submission Details */}
                          <div className="submission-details">
                            <div className="detail-item">
                              <Calendar size={16} />
                              <span className="detail-text">
                                {submission.submission_deadline ? 
                                  `Due ${formatDate(submission.submission_deadline)}` : 
                                  'No deadline specified'
                                }
                              </span>
                            </div>
                            
                            {submission.submission_deadline && (
                              <div className="detail-item">
                                <Clock size={16} />
                                <span className={`detail-text ${getDeadlineClass(submission.submission_deadline)}`}>
                                  {getDaysUntilDeadline(submission.submission_deadline)} days left
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="submission-actions">
                            <Link
                              to={`/submissions/${submission.submission_id}/apply`}
                              className="btn-primary"
                            >
                              {submission.has_draft ? 'Continue Application' : 'Apply Now'}
                            </Link>
                            <Link
                              to={`/submissions/${submission.submission_id}`}
                              className="btn-secondary"
                            >
                              <Eye size={16} />
                              View Details
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {hasMore && (
                  <div className="flex justify-center mt-6">
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="submission-btn submission-btn-primary"
                    >
                      Load More Submissions
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar - My Applications */}
            <div className="submission-content-card">
              <div className="submission-content-card-header">
                <h2 className="submission-content-card-title">My Applications</h2>
              </div>
              
              <div className="submission-content-card-body">
                {myApplications.length === 0 ? (
                  <div className="submission-empty-state">
                    <FileText className="submission-empty-icon" />
                    <p className="submission-empty-description">No applications yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myApplications.slice(0, 5).map((application) => (
                      <div key={application.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 text-sm">
                            {application.title}
                          </h4>
                          <span className={`submission-status-badge ${application.status}`}>
                            {getStatusIcon(application.status)}
                            {application.status}
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-2">
                          {application.theme}
                        </div>
                        
                        <div className="flex items-center justify-between text-sm mb-3">
                          <span className="text-green-600 font-medium">${application.submission_price}</span>
                          <span className={`text-sm ${getDeadlineColor(application.submission_deadline).includes('red') ? 'text-red-600' : getDeadlineColor(application.submission_deadline).includes('orange') ? 'text-orange-600' : 'text-gray-600'}`}>
                            {formatDeadline(application.submission_deadline)}
                          </span>
                        </div>
                        
                        <div className="mt-3">
                          <Link
                            to={`/submissions/${application.submission_id}/apply`}
                            className="submission-btn submission-btn-secondary w-full text-center"
                          >
                            {application.status === 'draft' ? 'Continue' : 'View'}
                          </Link>
                        </div>
                      </div>
                    ))}
                    
                    {myApplications.length > 5 && (
                      <div className="text-center pt-4">
                        <Link
                          to="/artist/applications"
                          className="submission-btn submission-btn-secondary w-full"
                        >
                          View All Applications ({myApplications.length})
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        </div>
      </div>
    </div>
  );
};

export default ArtistSubmissionDashboard;
